import {
  ProductVariantCalculator,
  IProductSelectionAvailabilityMap,
} from '@wix/wixstores-client-core/dist/es/src/productVariantCalculator/ProductVariantCalculator';
import type {IProduct} from '../types/galleryTypes';
import {ISliderProduct} from '../types/sliderGalleryTypes';
import type {ControllerFlowAPI} from '@wix/yoshi-flow-editor';

export interface ProductVariantInfo {
  variantSelectionIds: number[];
  selectionsAvailability: IProductSelectionAvailabilityMap;
  mediaItems: IProduct['media'];
  priceInfo: Pick<IProduct, 'formattedPrice' | 'price' | 'comparePrice' | 'formattedComparePrice'>;
}

export interface ProductsVariantInfoMap {
  [productId: string]: ProductVariantInfo;
}

export class ProductsOptionsService {
  private readonly productsMap: {
    [productId: string]: {
      variantCalculator: ProductVariantCalculator;
    };
  } = {};

  public constructor(private readonly optionalModifiersEnabled: boolean) {}

  public addProducts(products: IProduct[]): void {
    products.forEach((product) => {
      if ((product as ISliderProduct)?.isFake) {
        return;
      }

      if (this.productsMap[product.id]) {
        this.productsMap[product.id].variantCalculator.updateProduct(product);
        return;
      }

      this.productsMap[product.id] = {
        variantCalculator: this.optionalModifiersEnabled
          ? new ProductVariantCalculator(product, true)
          : new ProductVariantCalculator(product),
      };
    });
  }

  public handleUserInput(productId: string, selectionIds: number[]): void {
    this.productsMap[productId].variantCalculator.setVariantSelectionIds(selectionIds);
  }

  public clearSelectionsForProduct(productId: string): void {
    this.productsMap[productId].variantCalculator.setVariantSelectionIds([]);
  }

  public getVariantInfoMap(
    includeVariantDiscount?: boolean,
    options?: {isClientFormatting: boolean; flowAPI: ControllerFlowAPI}
  ): ProductsVariantInfoMap {
    return Object.keys(this.productsMap).reduce((result, productId) => {
      const calculator = this.productsMap[productId].variantCalculator;
      const variantInfo: ProductVariantInfo = {
        variantSelectionIds: [...calculator.getVariantSelectionIds()],
        selectionsAvailability: calculator.getSelectionsAvailability(),
        mediaItems: calculator.getMediaItems() as IProduct['media'],
        priceInfo: this.getPriceInfo(calculator, includeVariantDiscount, options),
      };

      return {
        ...result,
        [productId]: variantInfo,
      };
    }, {});
  }

  private getPriceInfo(
    calculator: ProductVariantCalculator,
    includeVariantDiscount?: boolean,
    options?: {isClientFormatting: boolean; flowAPI: ControllerFlowAPI}
  ) {
    return options?.isClientFormatting
      ? calculator.getFormattedPriceInfo(options.flowAPI, includeVariantDiscount)
      : calculator.getPriceInfo(includeVariantDiscount);
  }

  public getVariantSelectionIds(productId: string): number[] {
    return [...this.productsMap[productId].variantCalculator.getVariantSelectionIds()];
  }

  public canAddToCart(product: IProduct): boolean {
    return this.productsMap[product.id].variantCalculator.canAddToCart();
  }
}
